<script setup lang="ts">
import { ref, onBeforeMount, toRefs } from 'vue';
import Dialog from 'primevue/dialog';
import { required } from '@vuelidate/validators';
import { useRouting, useValidation } from '@/composables';
import { EMIT } from '@/enum';
import { IntegrationSchoolDayService } from '@/services';
import { useTenantStore, useTenantIntegrationStore } from '@/store';
import { OrganizationInterface } from '@/types';

interface PropsInterface {
  connection?: null;
  integration?: null;
  tenantId: number | null;
}

interface PayloadInterface {
  baseOrganization: null | number;
  sisOrganization: null | {
    id: string;
    nces_id: string;
    school_number: string;
    name: string;
    type: string;
    status: string;
    modified_at: string;
  };
}

const props = withDefaults(defineProps<PropsInterface>(), {
  connection: null,
  integration: null,
  tenantId: null,
});

const tenantStore = useTenantStore();

const tenantIntegrationStore = useTenantIntegrationStore();

const $routing = useRouting();

const $validation = useValidation();

const { connection, integration, tenantId } = toRefs(props);

const emit = defineEmits([EMIT.CANCEL, EMIT.UPDATE]);

const baseOrganizations: OrganizationInterface[] = ref([]);

const sisOrganizations = ref([]);

const modalVisible = ref(true);

const payload: PayloadInterface = ref({
  baseOrganization: null,
  sisOrganization: null,
});

const rules = {
  baseOrganization: { required },
  sisOrganization: { required },
};

const $v = $validation.defineValidation(rules, payload);

const submitted = ref(false);

onBeforeMount(async () => init());

const init = async () => {
  await defineOrganizations();
  await definePayload();
};

const defineOrganizations = async () => {
  const response = await IntegrationSchoolDayService.organizations(Number($routing.route.params.id));
  const integrationOrganizations = integration.value.integration_organizations;
  const isUpdateModal = tenantIntegrationStore.modalAction === 'update';

  baseOrganizations.value = tenantStore.tenant?.organizations.filter((item) => {
    if (isUpdateModal && connection.value && connection.value.organization_id === item.id) return item;
    if (!integrationOrganizations.find((aItem) => aItem.organization_id === item.id)) return item;
  });

  sisOrganizations.value = response.data.data.filter((item) => {
    if (isUpdateModal && connection.value && connection.value.organization_sis_id === item.id) return item;
    if (!integrationOrganizations.find((aItem) => aItem.organization_sis_id === item.id)) return item;
  });
};

const definePayload = async () => {
  if (connection.value) {
    payload.value = {
      baseOrganization: connection.value.organization_id,
      sisOrganization: sisOrganizations.value.find((item) => item.id === connection.value.organization_sis_id),
    };
  }
};

const onCancel = () => {
  modalVisible.value = false;
  $validation.resetDirty();
  emit(EMIT.CANCEL);
};

const onSave = async () => {
  submitted.value = true;
  const payloadData = {
    organization_id: payload.value.baseOrganization,
    organization_sis: {
      sis_id: payload.value.sisOrganization.id,
      nces_id: payload.value.sisOrganization.nces_id,
      school_number: payload.value.sisOrganization.school_number,
      name: payload.value.sisOrganization.name,
      type: payload.value.sisOrganization.type,
      status: payload.value.sisOrganization.status,
      modified_at: payload.value.sisOrganization.modified_at,
    },
  };

  connection.value
    ? await tenantIntegrationStore.updateTenantIntegrationOrganization(
        tenantId.value,
        integration.value.id,
        connection.value.id,
        payloadData,
      )
    : await tenantIntegrationStore.saveTenantIntegrationOrganization(tenantId.value, integration.value.id, payloadData);

  tenantIntegrationStore.openModal = false;
  tenantIntegrationStore.modalAction = 'create';
  onCancel();
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    :modal="true"
    header="Connect Organizations"
    :style="{ width: '600px' }"
    :draggable="false"
    @hide="onCancel"
  >
    <div class="card">
      <div class="field">
        <label class="required" for="baseOrganization">Organization</label>
        <Dropdown
          id="baseOrganization"
          v-model="$v.baseOrganization.$model"
          :options="baseOrganizations"
          option-label="name"
          option-value="id"
          empty-message="There are no organizations available to be selected"
          :filter="true"
          :virtual-scroller-options="{ itemSize: 35 }"
          :class="$validation.fieldStyles('baseOrganization', 'w-full')"
          placeholder="Select an Organization"
          :disabled="submitted"
        />
      </div>
    </div>
    <div class="field">
      <div class="flex p-2">
        <div class="flex w-4 mt-2 border-top-1 border-primary-500"></div>
        <div class="flex-grow-1 flex align-items-center justify-content-center">
          <span class="font-semibold text-600">Will Be Connected To</span>
        </div>
        <div class="flex w-4 mt-2 border-top-1 border-primary-500"></div>
      </div>
    </div>
    <div class="field">
      <label class="required" for="sisOrganization">SIS Organization</label>
      <Dropdown
        id="baseOrganization"
        v-model="$v.sisOrganization.$model"
        :options="sisOrganizations"
        option-label="name"
        empty-message="Organizations not available, verify the authentication credentials and try again"
        :filter="true"
        :virtual-scroller-options="{ itemSize: 35 }"
        :class="$validation.fieldStyles('sisOrganization', 'w-full')"
        placeholder="Select an SIS Organization"
        :disabled="submitted"
      />
    </div>
    <template #footer>
      <div class="flex justify-content-end">
        <AppButton type="secondary" text="Cancel" class="mr-2" :disabled="submitted" @click="onCancel" />
        <AppButton type="primary" text="Connect Organizations" :disabled="$v.$invalid || submitted" @click="onSave" />
      </div>
    </template>
  </Dialog>
</template>
